import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Wheat,
  Droplets,
  Users,
  ScrollText,
  MapPinned,
  MapPin,
  Check,
  Smartphone,
  Scale,
  ShieldCheck,
  RefreshCw,
  Sprout,
  ArrowRight,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { WaitlistDialog } from "@/components/WaitlistDialog";
import { FarmMap } from "@/components/FarmMap";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO =
  "https://customer-assets.emergentagent.com/job_silo-manager/artifacts/byh7ci35_image.png";

const img = (url, w = 1200) =>
  url.includes("unsplash")
    ? `${url}?auto=format&fit=crop&w=${w}&q=80`
    : `${url}?auto=compress&cs=tinysrgb&w=${w}`;

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/678604cc-f8b3-441a-bab0-503b63172c82/images/2026356bb241fd14fa88f75fcd3b609b1c4a677baae692ae1b80c51335a0aa62.png";

const FEATURES = [
  {
    icon: Wheat,
    title: "Every store, one place",
    desc: "Track grain, bale, straw, fertiliser, chemical and seed across all your sheds — each in the right unit (tonnes, bales or litres).",
    bullets: ["Grain, bale, fert, chemical & seed", "Tonnes, bales or litres per store", "Live balances by shed & location"],
    image: "https://static.prod-images.emergentagent.com/jobs/678604cc-f8b3-441a-bab0-503b63172c82/images/db88aaa841a3c90366450da8f41a9af6db6d0dc7dbf0ef1381e86713f28ac8bf.png",
  },
  {
    icon: Droplets,
    title: "Catch wet loads early",
    desc: "Log moisture on every load and AgriStock flags anything above UK safe-storage limits automatically.",
    bullets: ["Per-load moisture %", "Automatic 'needs drying' flags", "Protect your crop from spoilage"],
    image: img("https://images.pexels.com/photos/7891849/pexels-photo-7891849.jpeg"),
  },
  {
    icon: Users,
    title: "Your whole team",
    desc: "Workers join your farm with a simple code and log loads straight from the field on their own phone.",
    bullets: ["Invite the team with one code", "Everyone logs to the same store", "See who put each load in"],
    image: img("https://images.pexels.com/photos/6465/man-field-smartphone-yellow.jpg"),
  },
  {
    icon: ScrollText,
    title: "Full movement log",
    desc: "Every tonne in and out is recorded — variety, store, moisture, price and who logged it. Edit or void anytime.",
    bullets: ["Complete in/out history", "Edit & void with an audit trail", "Sale price captured on dispatch"],
    image: img("https://images.pexels.com/photos/34555605/pexels-photo-34555605.jpeg"),
  },
  {
    icon: MapPinned,
    title: "Fields & yield",
    desc: "Tag harvest loads to the field they came from and see yield in t/ha — in hectares or acres, your choice.",
    bullets: ["Tag intake to a field", "Automatic yield in t/ha", "Hectares or acres, your call"],
    image: img("https://images.pexels.com/photos/27778150/pexels-photo-27778150.jpeg"),
  },
];

const VALUES = [
  { icon: Scale, title: "Tonnes & t/ha", desc: "Built in UK units" },
  { icon: ShieldCheck, title: "Safe storage", desc: "UK moisture limits" },
  { icon: RefreshCw, title: "Always in sync", desc: "Whole team, one store" },
  { icon: Sprout, title: "Arable first", desc: "Wheat, barley, OSR & more" },
];

const TIERS = [
  { range: "1–3 users", price: "£7", note: "per user · month" },
  { range: "4–8 users", price: "£6", note: "per user · month" },
  { range: "9+ users", price: "£5", note: "per user · month" },
];

const FAQS = [
  {
    q: "What can I track?",
    a: "Grain, bale, straw, fertiliser, chemical and seed — each in the right unit (tonnes, bales or litres), across as many stores and locations as you need.",
  },
  {
    q: "Does it work for moisture and drying?",
    a: "Yes. Log moisture on grain, bale and straw loads and AgriStock automatically flags anything above UK safe-storage limits so you know what needs drying before it spoils.",
  },
  {
    q: "Can my whole team use it?",
    a: "Yes. Workers join your farm with a simple code and log loads from their own phone — straight from the cab, the shed or the office. You'll see who logged each load.",
  },
  {
    q: "Is my data private to my farm?",
    a: "Completely. Each farm is its own secure workspace. Only people you invite with your join code can see or add to your records.",
  },
  {
    q: "What does it cost?",
    a: "You pay per active user, and it gets cheaper as your team grows — from £5/user a month. There's a 14-day free trial with no card needed.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const Logo = ({ className = "" }) => (
  <div className={`flex items-center gap-2.5 ${className}`}>
    <span className="text-xl font-extrabold tracking-tight">
      <span className="text-[#14401F]">Agri</span>
      <span className="text-[#2FA84F]">Stock</span>
    </span>
  </div>
);

export default function Landing() {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);

  useEffect(() => {
    axios
      .get(`${API}/waitlist/count`)
      .then((r) => setCount(r.data.total || 0))
      .catch(() => {});
  }, []);

  const join = () => setOpen(true);
  const signIn = () =>
    toast("The AgriStock app launches soon", {
      description: "Join the waitlist to get access the moment it opens.",
    });

  const social = 120 + count; // baseline so the count never reads empty

  return (
    <div className="min-h-screen bg-[#FCFCFA] text-[#1A1C18]">
      {/* NAV */}
      <header className="fixed top-0 z-40 w-full border-b border-[#E8E5DF]/70 bg-[#FCFCFA]/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5">
          <Logo />
          <div className="flex items-center gap-2">
            <Button
              data-testid="nav-signin-btn"
              onClick={signIn}
              variant="ghost"
              className="rounded-full px-5 font-semibold text-[#14401F] hover:bg-[#E6EBE1] hover:text-[#14401F]"
            >
              Sign in
            </Button>
            <Button
              data-testid="nav-join-btn"
              onClick={join}
              className="rounded-full bg-[#14401F] px-5 font-semibold text-white hover:bg-[#0d2d15]"
            >
              Join the waitlist
            </Button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative flex min-h-[100svh] items-center overflow-hidden pt-20">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Combine harvesting a UK wheat field" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d2614]/95 via-[#14401F]/80 to-[#14401F]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d2614]/80 to-transparent" />
        </div>

        <div className="relative mx-auto w-full max-w-6xl px-5 py-24">
          <motion.div initial="hidden" animate="show" variants={fadeUp} className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-[#E6EBE1] backdrop-blur-sm">
              <Wheat className="h-3.5 w-3.5" /> Built for UK arable farms
            </span>
            <h1 className="mt-6 text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Smart bale, grain &amp; fertiliser management.
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-[#E6EBE1]/90 sm:text-lg">
              One simple place for UK farms to track every store — grain, bale, straw, fertiliser,
              chemical and seed — by quantity, location and moisture.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button
                data-testid="hero-join-btn"
                onClick={join}
                className="group rounded-full bg-[#2FA84F] px-7 py-6 text-base font-semibold text-white shadow-lg shadow-[#2FA84F]/30 transition-colors hover:bg-[#268a41]"
              >
                Join the waitlist
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button
                data-testid="hero-signin-btn"
                onClick={signIn}
                variant="outline"
                className="rounded-full border-white/40 bg-white/5 px-7 py-6 text-base font-semibold text-white backdrop-blur-sm hover:bg-white/15 hover:text-white"
              >
                Sign in
              </Button>
            </div>
            <p
              data-testid="hero-social-proof"
              className="mt-8 flex items-center gap-2 text-sm font-medium text-[#E6EBE1]"
            >
              <Wheat className="h-4 w-4 text-[#2FA84F]" />
              {social.toLocaleString()} farmers already on the list
            </p>
          </motion.div>
        </div>
      </section>

      {/* WHAT IS AGRISTOCK */}
      <section className="mx-auto max-w-3xl px-5 py-24 text-center">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
          <span className="text-xs font-bold uppercase tracking-widest text-[#8A9A7B]">
            What is AgriStock?
          </span>
          <p className="mt-5 text-base leading-relaxed text-[#1A1C18]/70 sm:text-lg">
            AgriStock is the all-in-one store record book for UK arable farms. Track grain, bale,
            straw, fertiliser, chemical and seed across every shed and store — what comes in, what
            goes out, who logged it, and whether it's safe to store — all from your phone.
          </p>
        </motion.div>
      </section>

      {/* FEATURES */}
      <section className="mx-auto max-w-6xl px-5 pb-8">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={fadeUp}
          className="mb-16 max-w-2xl"
        >
          <span className="text-xs font-bold uppercase tracking-widest text-[#14401F]">
            Powerful features, all in one place
          </span>
          <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#1A1C18] sm:text-4xl">
            Everything your grain store needs
          </h2>
        </motion.div>
        <div className="space-y-20 sm:space-y-28">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            const reverse = i % 2 === 1;
            return (
              <motion.div
                key={f.title}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={fadeUp}
                data-testid={`feature-block-${i}`}
                className={`flex flex-col items-center gap-10 lg:gap-16 ${
                  reverse ? "lg:flex-row-reverse" : "lg:flex-row"
                }`}
              >
                <div className="w-full lg:w-1/2">
                  <div className="overflow-hidden rounded-[20px] border border-[#E8E5DF] shadow-sm">
                    <img
                      src={f.image}
                      alt={f.title}
                      className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105"
                    />
                  </div>
                </div>
                <div className="w-full lg:w-1/2">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#E6EBE1]">
                    <Icon className="h-6 w-6 text-[#14401F]" />
                  </div>
                  <h3 className="mt-5 text-2xl font-bold tracking-tight text-[#14401F] sm:text-3xl">
                    {f.title}
                  </h3>
                  <p className="mt-3 text-base leading-relaxed text-[#1A1C18]/65">{f.desc}</p>
                  <ul className="mt-6 space-y-3">
                    {f.bullets.map((b) => (
                      <li key={b} className="flex items-center gap-3 text-[15px] text-[#1A1C18]">
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#2FA84F]">
                          <Check className="h-3 w-3 text-white" strokeWidth={3} />
                        </span>
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* FARM MAP */}
      <section className="mx-auto max-w-6xl px-5 py-24">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={fadeUp}
          className="mb-10 max-w-2xl"
        >
          <span className="text-xs font-bold uppercase tracking-widest text-[#14401F]">
            See it on the map
          </span>
          <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#1A1C18] sm:text-4xl">
            Track where your products are
          </h2>
          <p className="mt-4 text-base leading-relaxed text-[#1A1C18]/65 md:text-lg">
            Every grain store, fertiliser shed, bale stack, chemical store and fuel tank, pinned on a
            map of your farm. Tap one to see exactly what's inside — instantly.
          </p>
        </motion.div>
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
          <FarmMap />
        </motion.div>
      </section>


      {/* AVAILABILITY STRIP */}
      <section className="mt-24 bg-[#14401F]">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-center gap-3 px-5 py-8 text-center sm:flex-row">
          <Smartphone className="h-6 w-6 text-[#E6EBE1]" />
          <p className="text-lg font-semibold text-white">
            Works on any iPhone, Android and the web — log loads from the cab, the shed or the office.
          </p>
        </div>
      </section>

      {/* VALUES */}
      <section className="mx-auto max-w-6xl px-5 py-24">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={fadeUp}
          className="mb-12 max-w-2xl"
        >
          <span className="text-xs font-bold uppercase tracking-widest text-[#14401F]">
            Why farmers choose us
          </span>
          <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#1A1C18] sm:text-4xl">
            Built for UK arable farms
          </h2>
        </motion.div>
        <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
          {VALUES.map((v, i) => {
            const Icon = v.icon;
            return (
              <motion.div
                key={v.title}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ delay: i * 0.08 }}
                className="rounded-[20px] border border-[#E8E5DF] bg-[#F5F2ED] p-6"
              >
                <Icon className="h-7 w-7 text-[#14401F]" />
                <h4 className="mt-4 text-lg font-bold text-[#14401F]">{v.title}</h4>
                <p className="mt-2 text-sm leading-relaxed text-[#1A1C18]/60">{v.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* PRICING */}
      <section className="mx-auto max-w-5xl px-5 pb-24">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={fadeUp}
          className="mb-10 max-w-2xl"
        >
          <span className="text-xs font-bold uppercase tracking-widest text-[#14401F]">
            Simple, fair pricing
          </span>
          <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#1A1C18] sm:text-4xl">
            One plan, priced per user
          </h2>
        </motion.div>
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
          <div className="overflow-hidden rounded-[24px] border border-[#E8E5DF] bg-white">
            <div className="grid lg:grid-cols-5">
              <div className="bg-[#14401F] p-8 lg:col-span-2 lg:p-10">
                <span className="text-xs font-bold uppercase tracking-widest text-[#2FA84F]">
                  Simple pricing
                </span>
                <p className="mt-4 text-4xl font-extrabold text-white">
                  from £5<span className="text-xl font-semibold text-[#E6EBE1]/70">/user · mo</span>
                </p>
                <p className="mt-4 text-sm leading-relaxed text-[#E6EBE1]/80">
                  One plan, priced per active user — and it gets cheaper as your team grows.
                </p>
                <div className="mt-7 space-y-3">
                  {TIERS.map((t) => (
                    <div
                      key={t.range}
                      className="flex items-center justify-between rounded-xl bg-white/10 px-4 py-3"
                    >
                      <span className="text-sm font-medium text-[#E6EBE1]">{t.range}</span>
                      <span className="text-sm font-bold text-white">
                        {t.price}
                        <span className="ml-1 font-normal text-[#E6EBE1]/60">/user/mo</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-8 lg:col-span-3 lg:p-10">
                <h3 className="text-2xl font-bold text-[#14401F]">Everything included</h3>
                <ul className="mt-6 grid gap-4 sm:grid-cols-2">
                  {[
                    "In/out & moisture tracking",
                    "Whole team via join code",
                    "Unlimited grains & fields",
                    "Yield (t/ha) & CSV export",
                  ].map((b) => (
                    <li key={b} className="flex items-start gap-3 text-[15px] text-[#1A1C18]">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#2FA84F]">
                        <Check className="h-3 w-3 text-white" strokeWidth={3} />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
                <Button
                  data-testid="pricing-join-btn"
                  onClick={join}
                  className="mt-8 w-full rounded-full bg-[#14401F] py-6 text-base font-semibold text-white hover:bg-[#0d2d15] sm:w-auto sm:px-10"
                >
                  Join the waitlist
                </Button>
                <p className="mt-4 text-sm text-[#1A1C18]/55">
                  14-day free trial — no card needed. Billed monthly in GBP, cancel anytime. Save 2
                  months annually.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-5 pb-24">
        <span className="text-xs font-bold uppercase tracking-widest text-[#14401F]">
          Questions?
        </span>
        <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#1A1C18] sm:text-4xl">
          Frequently asked
        </h2>
        <Accordion type="single" collapsible className="mt-8 space-y-4" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              className="overflow-hidden rounded-[16px] border border-[#E8E5DF] bg-[#F5F2ED] px-5"
              data-testid={`faq-item-${i}`}
            >
              <AccordionTrigger className="py-5 text-left text-base font-bold text-[#1A1C18] hover:no-underline sm:text-lg">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="pb-5 text-[15px] leading-relaxed text-[#1A1C18]/70">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* FOUNDER QUOTE */}
      <section className="bg-[#F5F2ED]">
        <div className="mx-auto max-w-3xl px-5 py-20 text-center">
          <Wheat className="mx-auto h-8 w-8 text-[#2FA84F]" />
          <p className="mt-6 text-2xl font-medium leading-relaxed text-[#14401F] sm:text-3xl">
            "I built AgriStock for farms like ours — so the whole team always knows exactly what's
            in the store, how dry it is and what it's worth."
          </p>
          <p className="mt-6 text-sm font-semibold uppercase tracking-wider text-[#8A9A7B]">
            The AgriStock founder · UK arable
          </p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="mx-auto max-w-6xl px-5 py-24">
        <div className="relative overflow-hidden rounded-[28px] bg-[#14401F] px-6 py-16 text-center sm:px-12">
          <div className="absolute inset-0 opacity-15">
            <img src={HERO_IMG} alt="" className="h-full w-full object-cover" />
          </div>
          <div className="relative">
            <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Be first through the gate
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base text-[#E6EBE1]/85 md:text-lg">
              Join the waitlist and we'll let you know the moment AgriStock opens for your farm.
            </p>
            <Button
              data-testid="cta-join-btn"
              onClick={join}
              className="group mt-8 rounded-full bg-[#2FA84F] px-9 py-6 text-base font-semibold text-white shadow-lg shadow-[#2FA84F]/30 hover:bg-[#268a41]"
            >
              Join the waitlist
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#E8E5DF]">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-5 py-8 sm:flex-row">
          <Logo />
          <p className="text-sm text-[#1A1C18]/50">
            © {new Date().getFullYear()} AgriStock · Made for UK arable farms
          </p>
        </div>
      </footer>

      <WaitlistDialog open={open} onOpenChange={setOpen} onJoined={(t) => setCount(t)} />
    </div>
  );
}
